import type { Pricelist } from "@raumagarn/formatters";
import _get from "lodash/get";
import _isEqual from "lodash/isEqual";
import { useEffect, useMemo } from "react";
import { MicroCarts, useMicrocartStore } from "~/features/cart/MicroCart/MicroCartContext";
import { getPrice, usePricelistId } from "~/lib/hooks/usePrice";

export const useMicroCart = (
	id: string,
	items:
		| {
				[key: string]: unknown;
		  }[]
		| undefined,
	keys: {
		id: string;
		quantity: string;
		prices?: string;
	},
	callback?: () => void,
) => {
	const newItems = useMemo(
		() =>
			items?.map((item) => {
				return {
					id: _get(item, keys.id),
					quantity: _get(item, keys.quantity),
					comment: _get(item, "comment"),
					stock: item?.stock,
				};
			}) as unknown as {
				id: string;
				quantity: number;
				comment?: string;
				stock?: string;
			}[],
		[items, keys.id, keys.quantity],
	);
	const carts = useMicrocartStore((state) => state.carts);
	const registerCart = useMicrocartStore((state) => state.registerCart);
	const updateCart = useMicrocartStore((state) => state.updateCart);
	const isReset = useMicrocartStore((state) => state.isReset);
	const toggleReset = useMicrocartStore((state) => state.toggleReset);

	let totalPrice = 0;

	useEffect(() => {
		if (isReset) {
			toggleReset();
			callback?.();
		}
	}, [isReset, toggleReset, callback]);

	const pricelistId = usePricelistId();

	totalPrice =
		items?.reduce((acc, item) => {
			const pricelist = _get(item, keys?.prices || "prices") as unknown as Pricelist;

			let price = 0;
			if (id === MicroCarts.Recipe) {
				// Recipe prices are already processed PricelistItem objects
				price = (pricelist as any)?.priceAsNumber || 0;
			} else {
				// Other carts need price processing
				price = getPrice({ pricelist, pricelistId }).priceAsNumber || 0;
			}

			const quantity = _get(item, keys.quantity) as number;

			return acc + price * quantity;
		}, 0) || 0;

	const currentCart = carts.get(id);

	// Register
	useEffect(() => {
		if (!currentCart) return registerCart({ id, items: [], totalPrice });
	}, [registerCart, currentCart, totalPrice, id]);

	// Cart updater
	useEffect(() => {
		if (!currentCart) return;
		// Also update when only the price changes (e.g. the pricelist resolves async
		// after items are already stable), not just when the items themselves change.
		if (_isEqual(currentCart.items, newItems) && currentCart.totalPrice === totalPrice) return;

		updateCart({ id, items: newItems, totalPrice });
	}, [newItems, currentCart, id, totalPrice, updateCart]);
};
